import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import styles from "./BaseMap.module.scss";

/**
 * True while the element is near the viewport. It turns on 200px before the element comes into view and off
 * again once the element is 800px away, so a map that scrolls off frees its WebGL context but ordinary scroll
 * jitter never rebuilds it. Browsers cap live WebGL contexts per page (Chrome drops the oldest past ~16): the
 * hero, the route builder and the route cards would be 7 on the home page, and this keeps it to the few that
 * are actually on screen.
 */
function useNearViewport(ref: React.RefObject<Element>): boolean {
	const [near, setNear] = useState(false);
	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;
		if (typeof IntersectionObserver === "undefined") {
			setNear(true);
			return undefined;
		}
		const enter = new IntersectionObserver(([entry]) => entry.isIntersecting && setNear(true), { rootMargin: "200px 0px", threshold: 0 });
		const exit = new IntersectionObserver(([entry]) => !entry.isIntersecting && setNear(false), { rootMargin: "800px 0px", threshold: 0 });
		enter.observe(el);
		exit.observe(el);
		return () => {
			enter.disconnect();
			exit.disconnect();
		};
	}, [ref]);
	return near;
}

// Where every partner map's tiles, glyphs and sprite come from (partnerMapStyle.ts). Kept here, not imported from the
// style module, so the page's first bundle does not take the style JSON with it.
const STADIA_ORIGIN = "https://tiles.stadiamaps.com";

/**
 * Opens the connection to Stadia once per page, as soon as a map slot mounts: the TLS handshake then runs while the map's
 * chunk is still downloading, instead of after it, on the path to the first basemap pixel. `crossOrigin` because
 * MapLibre fetches tiles with CORS, and a preconnect only serves requests of the same mode.
 */
function preconnectTiles() {
	if (typeof document === "undefined" || document.querySelector(`link[rel="preconnect"][href="${STADIA_ORIGIN}"]`)) return;
	const link = document.createElement("link");
	link.rel = "preconnect";
	link.href = STADIA_ORIGIN;
	link.crossOrigin = "anonymous";
	document.head.appendChild(link);
}

/**
 * Where a partner page's map goes: kept out of server rendering and out of the DOM until its slot is near the
 * screen. `children` is the lazy map (a partner's own chunk: MapLibre is big and browser-only, so it is never part
 * of the page's first paint). It fills its parent; the placeholder holds the same space, so nothing moves when
 * the map arrives. `mounted` is what keeps the server's HTML and the first client render identical
 * (.claude/docs/marketing-ssr.md). `still`: the map is a picture in a card that is itself a link, so it takes
 * no clicks of its own.
 */
const MapSlot = ({ still, children }: { still?: boolean; children: ReactNode }) => {
	const ref = useRef<HTMLDivElement>(null);
	const [mounted, setMounted] = useState(false);
	const near = useNearViewport(ref);
	useEffect(() => {
		setMounted(true);
		preconnectTiles();
	}, []);

	return (
		<div ref={ref} className={still ? `${styles.slot} ${styles.still}` : styles.slot}>
			{mounted && near ? (
				<Suspense fallback={<div className={styles.placeholder} />}>{children}</Suspense>
			) : (
				<div className={styles.placeholder} aria-hidden="true" />
			)}
		</div>
	);
};

export default MapSlot;
